import { Navigation } from "@/components/Navigation"
import { Footer } from "@/components/Footer"
import { ArrowRight } from "lucide-react"

const featuredArticle = {
    category: "Featured Launch",
    title: "RSA Tech Group Launches LeoRix AI",
    excerpt:
        "The definitive Enterprise Hub for transforming your entire Lead-to-Revenue lifecycle with autonomous AI agents.",
    date: "Dec 6, 2025",
    author: "Product Team",
    image: "/LEORIX_Hey%20Leo(logo)@300x-100.jpg",
    link: "https://leorix.com",
}

const allInsights = [
    {
        category: "Product Launch",
        title: "Introducing Postwyse: Intelligent Marketing",
        excerpt:
            "Revolutionize your marketing content management with our new AI-driven platform for automated creation and distribution.",
        date: "Dec 24, 2025",
        author: "Product Team",
        image: "/postwyse1.png",
        link: "https://postwyse.com",
    },
    {
        category: "Artificial Intelligence",
        title: "Generative AI in the Enterprise: Beyond the Hype",
        excerpt:
            "How leading organizations are moving from pilot programs to scalable production deployments of LLMs.",
        date: "Oct 12, 2024",
        author: "RSA Tech Group Labs",
        image: "https://images.unsplash.com/photo-1677442136019-21780ecad995?auto=format&fit=crop&q=80&w=800",
    },
    {
        category: "Cloud Strategy",
        title: "Optimizing Multi-Cloud Architectures for Cost & Scale",
        excerpt:
            "Strategies for managing complexity and controlling spend in hybrid cloud environments.",
        date: "Sep 28, 2024",
        author: "Cloud Engineering Team",
        image: "https://images.unsplash.com/photo-1451187580459-43490279c0fa?auto=format&fit=crop&q=80&w=800",
    },
    {
        category: "Digital Transformation",
        title: "The Future of Fintech: Predictive Analytics at the Edge",
        excerpt:
            "Real-time decision making in financial services using edge computing and AI.",
        date: "Sep 15, 2024",
        author: "Fintech Practice",
        image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&q=80&w=800",
    },
    {
        category: "Cybersecurity",
        title: "Zero Trust Architecture: Implementation Guide 2025",
        excerpt:
            "A comprehensive guide to securing distributed workforces and cloud-native applications.",
        date: "Aug 30, 2024",
        author: "Security Operations",
        image: "https://images.unsplash.com/photo-1550751827-4bd374c3f58b?auto=format&fit=crop&q=80&w=800",
    },
    {
        category: "Data Science",
        title: "Unlocking Value from Unstructured Data",
        excerpt:
            "Leveraging NLP and computer vision to turn documents and images into actionable insights.",
        date: "Aug 15, 2024",
        author: "Data Science Team",
        image: "https://images.unsplash.com/photo-1504868584819-f8e8b4b6d7e3?auto=format&fit=crop&q=80&w=800",
    },
    {
        category: "Industry 4.0",
        title: "Digital Twins in Manufacturing",
        excerpt:
            "Simulation and predictive maintenance use cases driving efficiency in smart factories.",
        date: "Aug 01, 2024",
        author: "IoT Practice",
        image: "https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?auto=format&fit=crop&q=80&w=800",
    },
]

import { SEO } from "@/components/SEO"
import { PageFAQ } from "@/components/PageFAQ"
import { newsInsightsFAQs } from "@/data/faqData"

const NewsInsights = () => {
    return (
        <div className="min-h-screen bg-white">
            <SEO
                title="News Room | Insights & Perspectives | RSA Tech Group"
                description="Expert perspectives on the technologies and strategies shaping the future of enterprise. Launching LeoRix AI, Postwyse, and more."
                image="/news-hero-bg.png"
            />
            <Navigation />

            {/* Hero Section */}
            <div className="relative pt-48 pb-24 overflow-hidden bg-[#0f1629]">
                <div
                    className="absolute inset-0 z-0 bg-cover bg-center bg-no-repeat"
                    style={{ backgroundImage: "url('/newsroom_light_bg.png')" }}
                >
                    <div className="absolute inset-0 bg-[#0f1629]/80 backdrop-blur-[2px]" />
                </div>
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_50%_at_50%_30%,rgba(28,43,110,0.2),transparent_60%)]" />
                <div className="absolute bottom-0 right-0 w-[400px] h-[400px] bg-[#FDA400]/[0.04] rounded-full blur-[120px]" />

                <div className="section-container relative z-10 text-center">
                    <div className="max-w-4xl mx-auto">
                        <p className="text-[#FDA400] text-sm font-semibold uppercase tracking-[0.2em] mb-6">
                            Latest Updates
                        </p>
                        <h1 className="text-4xl md:text-6xl lg:text-7xl font-black text-white mb-6 leading-[1.1] tracking-tight">
                            News{" "}
                            <span className="text-[#FDA400]">Room</span>
                        </h1>
                        <p className="text-xl md:text-2xl text-slate-300 max-w-3xl mx-auto mb-10 font-light leading-relaxed">
                            Expert perspectives on the technologies and strategies shaping the future of enterprise.
                        </p>
                        <p className="text-xs text-slate-500 uppercase tracking-[0.25em] font-semibold">
                            Insights, Launches & Industry Perspectives
                        </p>
                    </div>
                </div>
            </div>

            {/* Featured Article */}
            <section className="py-24 bg-gradient-to-b from-slate-50 via-white to-slate-50 relative overflow-hidden">
                {/* Dot grid bg */}
                <div
                    className="absolute inset-0 opacity-[0.25]"
                    style={{
                        backgroundImage: "radial-gradient(circle, #1C2B6E 0.5px, transparent 0.5px)",
                        backgroundSize: "28px 28px",
                    }}
                />
                <div className="absolute top-0 left-1/3 w-[500px] h-[500px] bg-[#1C2B6E]/[0.02] rounded-full blur-[150px]" />
                <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] bg-[#FDA400]/[0.02] rounded-full blur-[120px]" />

                <div className="section-container relative z-10">
                    {/* Section header */}
                    <div className="text-center mb-16">
                        <p className="text-[#FDA400] text-sm font-semibold uppercase tracking-[0.2em] mb-4">
                            Spotlight
                        </p>
                        <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-[#1C2B6E] tracking-tight leading-[1.1]">
                            Featured{" "}
                            <span className="text-[#FDA400]">Launch</span>
                        </h2>
                    </div>

                    {/* Featured card */}
                    <a
                        href={featuredArticle.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="partner-card group rounded-3xl overflow-hidden shadow-[0_2px_8px_rgba(0,0,0,0.04)] hover:shadow-[0_25px_80px_rgba(28,43,110,0.15)] transition-all duration-500 hover:-translate-y-2 block mb-20"
                    >
                        {/* Shine sweep */}
                        <div className="absolute inset-0 z-20 overflow-hidden rounded-3xl pointer-events-none">
                            <div className="absolute inset-0 w-1/3 bg-gradient-to-r from-transparent via-white/20 to-transparent opacity-0 group-hover:opacity-100 group-hover:animate-[shine_0.8s_ease-in-out]" />
                        </div>

                        {/* Top accent bar */}
                        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#FDA400] via-[#f5b800] to-[#FDA400] z-10" />

                        {/* Glow orbs */}
                        <div className="absolute -top-20 -right-20 w-60 h-60 bg-[#FDA400]/[0.05] rounded-full blur-[80px] group-hover:bg-[#FDA400]/[0.12] group-hover:scale-110 transition-all duration-700" />
                        <div className="absolute -bottom-16 -left-16 w-48 h-48 bg-[#1C2B6E]/[0.04] rounded-full blur-[60px] group-hover:bg-[#1C2B6E]/[0.1] transition-all duration-700" />

                        <div className="grid lg:grid-cols-2 h-full relative z-10">
                            {/* Image side */}
                            <div className="h-64 lg:h-[420px] overflow-hidden relative">
                                <img
                                    src={featuredArticle.image}
                                    alt={featuredArticle.title}
                                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                                />
                                <div className="absolute inset-0 bg-gradient-to-r from-transparent to-white/10 lg:bg-gradient-to-l lg:from-transparent lg:to-transparent" />
                            </div>

                            {/* Content side */}
                            <div className="p-8 md:p-12 lg:p-14 flex flex-col justify-center">
                                <span className="text-[10px] font-bold text-[#FDA400] uppercase tracking-[0.2em] bg-[#FDA400]/10 border border-[#FDA400]/20 px-4 py-2 rounded-full self-start mb-8 backdrop-blur-sm">
                                    {featuredArticle.category}
                                </span>

                                <h3 className="text-3xl md:text-4xl lg:text-5xl font-black text-[#1C2B6E] tracking-tight mb-5 leading-[1.1]">
                                    {featuredArticle.title}
                                </h3>

                                <p className="text-slate-500 text-lg leading-relaxed font-light mb-8 max-w-xl">
                                    {featuredArticle.excerpt}
                                </p>

                                <div className="flex items-center gap-6 pt-6 border-t border-slate-200/60">
                                    <div className="flex items-center gap-3">
                                        <span className="text-xs text-slate-400 uppercase tracking-wider font-medium">
                                            {featuredArticle.date}
                                        </span>
                                        <span className="w-1 h-1 rounded-full bg-slate-300" />
                                        <span className="text-xs text-slate-400 uppercase tracking-wider font-medium">
                                            {featuredArticle.author}
                                        </span>
                                    </div>
                                    <span className="ml-auto text-[#FDA400] text-sm font-bold flex items-center gap-2 group-hover:gap-3 transition-all duration-300">
                                        Visit LeoRix <ArrowRight className="h-4 w-4" />
                                    </span>
                                </div>
                            </div>
                        </div>
                    </a>

                    {/* Section header for grid */}
                    <div className="text-center mb-16">
                        <p className="text-[#FDA400] text-sm font-semibold uppercase tracking-[0.2em] mb-4">
                            All Articles
                        </p>
                        <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-[#1C2B6E] tracking-tight leading-[1.1]">
                            Insights &{" "}
                            <span className="text-[#FDA400]">Perspectives</span>
                        </h2>
                    </div>

                    {/* Articles Grid */}
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-7xl mx-auto">
                        {allInsights.map((item, index) => (
                            <div
                                key={index}
                                className={`partner-card group rounded-3xl overflow-hidden shadow-[0_2px_8px_rgba(0,0,0,0.04)] hover:shadow-[0_25px_80px_rgba(28,43,110,0.12)] transition-all duration-500 hover:-translate-y-2 cursor-pointer ${
                                    index === 0 ? "lg:col-span-2" : ""
                                }`}
                                onClick={() =>
                                    item.link ? window.open(item.link, "_blank") : null
                                }
                            >
                                {/* Shine sweep */}
                                <div className="absolute inset-0 z-20 overflow-hidden rounded-3xl pointer-events-none">
                                    <div className="absolute inset-0 w-1/3 bg-gradient-to-r from-transparent via-white/20 to-transparent opacity-0 group-hover:opacity-100 group-hover:animate-[shine_0.8s_ease-in-out]" />
                                </div>

                                {/* Top accent */}
                                <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#FDA400] to-[#f5b800] scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left z-10" />

                                {/* Glow */}
                                <div className="absolute -bottom-12 -right-12 w-44 h-44 bg-[#FDA400]/[0.04] rounded-full blur-[50px] group-hover:bg-[#FDA400]/10 group-hover:scale-110 transition-all duration-700" />

                                {/* Ghost number */}
                                <span className="absolute top-4 right-6 text-8xl font-black text-[#1C2B6E]/[0.03] leading-none select-none group-hover:text-[#1C2B6E]/[0.07] transition-colors duration-500">
                                    {String(index + 1).padStart(2, "0")}
                                </span>

                                {index === 0 ? (
                                    /* Wide card layout for Postwyse */
                                    <div className="grid md:grid-cols-2 h-full relative z-10">
                                        <div className="h-56 md:h-full overflow-hidden relative">
                                            <img
                                                src={item.image}
                                                alt={item.title}
                                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                                            />
                                        </div>
                                        <div className="p-8 md:p-10 flex flex-col justify-center">
                                            <span className="text-[10px] font-bold text-[#FDA400] uppercase tracking-[0.2em] bg-[#FDA400]/10 border border-[#FDA400]/20 px-4 py-2 rounded-full self-start mb-6 backdrop-blur-sm">
                                                {item.category}
                                            </span>
                                            <h3 className="text-2xl md:text-3xl font-black text-[#1C2B6E] tracking-tight mb-3 leading-[1.1]">
                                                {item.title}
                                            </h3>
                                            <p className="text-slate-500 text-sm leading-relaxed font-light mb-6">
                                                {item.excerpt}
                                            </p>
                                            <div className="flex items-center gap-3 pt-5 border-t border-slate-100">
                                                <span className="text-xs text-slate-400 uppercase tracking-wider font-medium">
                                                    {item.date}
                                                </span>
                                                <span className="w-1 h-1 rounded-full bg-slate-300" />
                                                <span className="text-xs text-slate-400 uppercase tracking-wider font-medium">
                                                    {item.author}
                                                </span>
                                                {item.link && (
                                                    <span className="ml-auto text-[#FDA400] text-sm font-bold flex items-center gap-2 group-hover:gap-3 transition-all duration-300">
                                                        Read More <ArrowRight className="h-4 w-4" />
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    /* Standard card layout */
                                    <div className="h-full flex flex-col relative z-10">
                                        <div className="h-52 overflow-hidden relative">
                                            <img
                                                src={item.image}
                                                alt={item.title}
                                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                                            />
                                            <div className="absolute inset-0 bg-gradient-to-t from-white/20 to-transparent" />
                                        </div>
                                        <div className="p-8 md:p-10 flex flex-col flex-grow">
                                            <span className="text-[10px] font-bold text-[#FDA400] uppercase tracking-[0.2em] bg-[#FDA400]/10 border border-[#FDA400]/20 px-4 py-2 rounded-full self-start mb-5 backdrop-blur-sm">
                                                {item.category}
                                            </span>

                                            <h3 className="text-xl md:text-2xl font-black text-[#1C2B6E] tracking-tight mb-3 leading-[1.15]">
                                                {item.title}
                                            </h3>

                                            <p className="text-slate-500 text-sm leading-relaxed font-light mb-6 flex-grow">
                                                {item.excerpt}
                                            </p>

                                            <div className="flex items-center gap-3 pt-5 border-t border-slate-100 mt-auto">
                                                <span className="text-xs text-slate-400 uppercase tracking-wider font-medium">
                                                    {item.date}
                                                </span>
                                                <span className="w-1 h-1 rounded-full bg-slate-300" />
                                                <span className="text-xs text-slate-400 uppercase tracking-wider font-medium">
                                                    {item.author}
                                                </span>
                                                <span className="ml-auto text-[#FDA400] text-sm font-bold flex items-center gap-2 group-hover:gap-3 transition-all duration-300">
                                                    Read <ArrowRight className="h-4 w-4" />
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <PageFAQ
                faqs={newsInsightsFAQs}
                title="News & Insights FAQs"
                subtitle="Common questions about RSA Tech Group news and updates"
                variant="muted"
            />

            <Footer />
        </div>
    )
}

export default NewsInsights
